import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { FORGE_MASTER_LABEL_PATH } from '../../paths';
import { RemoteDataCollectionCandidateGroup, RemoteDataCollectionReferenceCrop } from '../../types';
import {
  CropStatusBadge,
  getCropOrientationBadgeLabel,
  getCropOrientationBadgeStyles,
  getCropSourceBadgeLabel,
  getCropSourceBadgeStyles,
} from './CropStatusBadge';

type CandidatePreviewItem = {
  itemNumber?: string | null;
  lookupValue?: string | null;
  normalizedUpc?: string | null;
  productImageUrl?: string | null;
  productName?: string | null;
  upc?: string | null;
};

function formatPercent(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return `${Math.round(value * 1000) / 10}%`;
}

function masterLabelUrl(
  chainId: number,
  itemNumber?: string | null,
  normalizedBarcode?: string | null
): string {
  const params = new URLSearchParams({ chain: String(chainId) });
  if (itemNumber) params.set('in', itemNumber);
  else if (normalizedBarcode) params.set('nb', normalizedBarcode);
  return `${FORGE_MASTER_LABEL_PATH}?${params.toString()}`;
}

function candidateBarcode(group: RemoteDataCollectionCandidateGroup): string | null {
  return (
    group.normalizedBarcodes[0] ||
    group.candidates.find((candidate) => candidate.normalizedBarcode)?.normalizedBarcode ||
    (group.keyKind === 'barcode' ? group.key : null)
  );
}

function safeImageUrl(url?: string | null): string | null {
  if (!url) return null;
  const encodeUnsafeUrlChars = (value: string) =>
    value.replaceAll('[', '%5B').replaceAll(']', '%5D').replaceAll(' ', '%20');
  try {
    return encodeUnsafeUrlChars(new URL(url).toString());
  } catch {
    return encodeUnsafeUrlChars(url);
  }
}

function ReferenceCropSampleThumbnail({
  crop,
  imageUrl,
  index,
  failed,
  onError,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  crop: RemoteDataCollectionReferenceCrop;
  imageUrl?: string | null;
  index: number;
  failed: boolean;
  onError: () => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}) {
  const sourceBadgeLabel = getCropSourceBadgeLabel(crop.source);
  const orientationBadgeLabel =
    crop.classificationType === 'product' ? getCropOrientationBadgeLabel(crop.orientation) : null;
  const cropZoomMeta: ZoomMeta = {
    id: crop.cropId,
    source: crop.source ?? undefined,
    normalizedBarcode: crop.normalizedBarcode ?? undefined,
    classificationType: crop.classificationType ?? undefined,
  };

  return (
    <div
      data-testid={`mdc-sample-thumb-${index + 1}`}
      className={`mdc-sample-thumb ${!imageUrl || failed ? 'mdc-sample-thumb--empty' : ''}`}
      onMouseEnter={
        imageUrl && !failed && onImageHover
          ? (event) => onImageHover(event, imageUrl, cropZoomMeta)
          : undefined
      }
      onMouseMove={
        imageUrl && !failed && onImageMove
          ? (event) => onImageMove(event, imageUrl, cropZoomMeta)
          : undefined
      }
      onMouseLeave={imageUrl && !failed ? onImageLeave : undefined}
    >
      {imageUrl && !failed ? (
        <img
          src={imageUrl}
          alt={`Reference crop sample ${index + 1}`}
          className="mdc-sample-thumb-image"
          onError={onError}
        />
      ) : (
        'Unavailable'
      )}
      {sourceBadgeLabel ? (
        <CropStatusBadge
          label={sourceBadgeLabel}
          size={78}
          testId={`mdc-sample-source-${index + 1}`}
          style={{
            position: 'absolute',
            left: -4,
            bottom: -4,
            ...getCropSourceBadgeStyles(sourceBadgeLabel),
          }}
        />
      ) : null}
      {orientationBadgeLabel ? (
        <CropStatusBadge
          label={orientationBadgeLabel}
          size={78}
          testId={`mdc-sample-orientation-${index + 1}`}
          style={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            ...getCropOrientationBadgeStyles(orientationBadgeLabel),
          }}
        />
      ) : null}
    </div>
  );
}

export function ReferenceCropSampleRow({
  crop,
  index,
  designName,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  crop: RemoteDataCollectionReferenceCrop;
  index: number;
  designName?: string;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}) {
  const imageUrl = safeImageUrl(crop.vizUrl) || crop.vizUrl;
  const [failed, setFailed] = useState(false);
  const sampleTitle = designName ? `${index + 1}. ${designName}` : String(index + 1);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  return (
    <div className="mdc-sample-row">
      <ReferenceCropSampleThumbnail
        crop={crop}
        imageUrl={imageUrl}
        index={index}
        failed={failed}
        onError={() => setFailed(true)}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
      <div className="mdc-sample-meta">
        <div className="mdc-sample-title" title={sampleTitle}>
          {sampleTitle}
        </div>
        <div className="mdc-sample-subline">
          <span>
            {crop.similarity !== undefined && crop.similarity !== null
              ? formatPercent(crop.similarity)
              : '-'}
          </span>
        </div>
        <div className="mdc-sample-identity">
          <span className="mdc-sample-identity-label">nb</span>
          <span className="mdc-sample-identity-value">{crop.normalizedBarcode || '-'}</span>
        </div>
        <div className="mdc-sample-identity">
          <span className="mdc-sample-identity-label">d</span>
          <span className="mdc-sample-identity-value">{crop.designId || '-'}</span>
        </div>
      </div>
    </div>
  );
}

export function CandidatePreviewCard({
  group,
  item,
  referenceCrops,
  rank,
  chainId,
  storeId,
  assignLabel,
  assignDisabled,
  sampleDesignNamesById = {},
  showForgeLink = true,
  samplesLoading = false,
  onAssign,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  group: RemoteDataCollectionCandidateGroup;
  item?: CandidatePreviewItem | null;
  referenceCrops: RemoteDataCollectionReferenceCrop[];
  rank: number;
  chainId?: number;
  storeId?: number | null;
  assignLabel?: string;
  assignDisabled?: boolean;
  sampleDesignNamesById?: Record<string, string>;
  showForgeLink?: boolean;
  samplesLoading?: boolean;
  onAssign?: () => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const barcode = item?.normalizedUpc || candidateBarcode(group);
  const itemNumber = item?.itemNumber || group.itemNumber;
  const lookupValue =
    item?.lookupValue && item.lookupValue !== itemNumber && item.lookupValue !== barcode
      ? item.lookupValue
      : null;
  const embeddingsParams = new URLSearchParams();
  if (chainId) embeddingsParams.set('chain_id', String(chainId));
  if (storeId) embeddingsParams.set('store_id', String(storeId));
  if (itemNumber) embeddingsParams.set('item_number', itemNumber);
  else if (barcode) embeddingsParams.set('upc', barcode);
  const masterLabelHref =
    showForgeLink && chainId ? masterLabelUrl(chainId, itemNumber, barcode) : null;
  const sampleCrops = referenceCrops.slice(0, 3);
  const rankLabel = rank === 1 ? 'Best match' : rank === 2 ? 'Alternative' : 'Candidate';
  const productTitle = item?.productName || group.productName || itemNumber || group.key;
  const showItemNumber = Boolean(itemNumber && itemNumber !== productTitle);
  const productImageUrl = safeImageUrl(item?.productImageUrl);
  const [productImageFailed, setProductImageFailed] = useState(false);

  useEffect(() => {
    setProductImageFailed(false);
  }, [productImageUrl]);

  return (
    <article className={`mdc-candidate-card mdc-candidate-card--rank-${rank}`}>
      <div className="mdc-prediction-lane-top">
        <div className="mdc-prediction-rank">
          <strong>{rank}</strong>
          <span>{rankLabel}</span>
        </div>
        <span className="mdc-candidate-score-pill">{formatPercent(group.score)}</span>
      </div>
      <div className="mdc-candidate-body">
        <div className="mdc-candidate-title-row">
          <div className="mdc-candidate-title-stack">
            <div className="mdc-candidate-name">{productTitle}</div>
            {showItemNumber ? <div className="mdc-candidate-item-number">{itemNumber}</div> : null}
          </div>
          <div className="mdc-candidate-product-image-slot">
            {productImageUrl && !productImageFailed ? (
              <img
                src={productImageUrl}
                alt={productTitle}
                className="mdc-candidate-product-image"
                onMouseEnter={
                  onImageHover
                    ? (event) =>
                        onImageHover(event, productImageUrl, {
                          id: itemNumber || barcode || group.key,
                          normalizedBarcode: barcode ?? undefined,
                        })
                    : undefined
                }
                onMouseMove={
                  onImageMove
                    ? (event) =>
                        onImageMove(event, productImageUrl, {
                          id: itemNumber || barcode || group.key,
                          normalizedBarcode: barcode ?? undefined,
                        })
                    : undefined
                }
                onMouseLeave={onImageLeave}
                onError={() => setProductImageFailed(true)}
              />
            ) : null}
          </div>
        </div>
        <div className="mdc-candidate-identity">
          {lookupValue ? (
            <div>
              <span>Lookup</span>
              <strong>{lookupValue}</strong>
            </div>
          ) : null}
        </div>
        <div className="mdc-candidate-links">
          {onAssign ? (
            <button
              type="button"
              className="mdc-candidate-assign"
              disabled={assignDisabled}
              onClick={onAssign}
            >
              {assignLabel || 'Assign'}
            </button>
          ) : null}
          <div className="mdc-candidate-link-actions">
            {embeddingsParams.toString() ? (
              <Link
                to={`/embeddings/detail?${embeddingsParams.toString()}`}
                target="_blank"
                title="Open in Control embeddings"
                aria-label="Open in Control embeddings"
              >
                [c]
              </Link>
            ) : null}
            {masterLabelHref ? (
              <Link
                to={masterLabelHref}
                title="Open in Forge Master Label"
                aria-label="Open in Forge Master Label"
              >
                [f]
              </Link>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mdc-candidate-crops">
        <div className="mdc-candidate-crops-title">Top samples</div>
        {sampleCrops.length ? (
          <div className="mdc-sample-list">
            {sampleCrops.map((crop, index) => (
              <ReferenceCropSampleRow
                key={crop.cropId || `${group.key}:${index}`}
                crop={crop}
                index={index}
                designName={crop.designId ? sampleDesignNamesById[crop.designId] : undefined}
                onImageHover={onImageHover}
                onImageMove={onImageMove}
                onImageLeave={onImageLeave}
              />
            ))}
          </div>
        ) : samplesLoading ? (
          <div className="mdc-candidate-empty-copy">Loading reference samples...</div>
        ) : (
          <div className="mdc-candidate-empty-copy">No reference samples</div>
        )}
      </div>
    </article>
  );
}
